import { useEffect, useState } from "react";
import { IsNull } from "../utilities/util";
import SimpleMessageModal from "./SimpleNotification";
import ConfirmationModal from "./ConfirmationModal";
import { UpdateCommentrySummary, UpdateTagStatus } from "./apis/post";
import {
  X,
  Zap,
  Users,
  Tags,
  MessageSquare,
  Star,
  Layers,
  CheckCircle2,
  AlertCircle,
  Info,
  ChevronRight,
  ShieldCheck,
  Briefcase,
  Heart,
  Camera,
  RefreshCw,
  Send
} from 'lucide-react';

const tabs = [
  { id: "Summary", icon: <Info size={14} /> },
  { id: "Matched Tags", icon: <Tags size={14} /> },
  { id: "Commentary", icon: <MessageSquare size={14} /> },
  { id: "Astrology", icon: <Star size={14} /> },
  { id: "Cluster Analysis", icon: <Layers size={14} /> }
];

const AstroMatchModal = ({
  isOpen,
  onClose,
  data,
  matchedTags,
  summary,
  onPublish,
  onResetMatch,
  onViewOppositeMatch,
  onUpdateDecisionToYes,
  onComparisionProfileClick,
  onPhotoComparison
}) => {
  const [activeTab, setActiveTab] = useState("Summary");
  const [showModal, setShowModel] = useState(false);
  const [viewCurrent, setViewCurrent] = useState(true);
  const [confirmModal, setConfirmModal] = useState(false);
  const [decisionType, setDecisionType] = useState("");
  const [localMatchedTags, setLocalMatchedTags] = useState(matchedTags);
  const [editableSummary, setEditableSummary] = useState(summary);
  const [editInput, setEditInput] = useState("");
  const [editTitle, setEditTitle] = useState("");
  const [editingItem, setEditingItem] = useState(null);
  const [showEditModal, setShowEditModal] = useState(false);

  useEffect(() => {
    if (matchedTags) setLocalMatchedTags(matchedTags);
  }, [matchedTags]);

  useEffect(() => {
    if (summary) setEditableSummary(summary);
  }, [summary]);

  if (!isOpen || !data) return null;

  const {
    profile,
    astrology,
    astrologyCategories,
    clusterAnalysis,
    publish,
    commentary
  } = data;

  const handleConfirm = async (_candidateId) => {
    const status = await onUpdateDecisionToYes(_candidateId, decisionType);
    if (status) {
      setConfirmModal(false);
      window.location.reload();
    }
  };

  const onChangeTagStatus = async (tag, newStatus) => {
    const payload = { tagId: tag.tagId, status: newStatus };
    const res = await UpdateTagStatus(payload);
    if (res.status === "success") {
      setLocalMatchedTags((prev) =>
        prev.map((t) => t.tagId === tag.tagId ? { ...t, status: newStatus } : t)
      );
    }
  };

  const handleEditClick = (section, index, title, value) => {
    setEditingItem({ section, index });
    setEditTitle(title);
    setEditInput(value);
    setShowEditModal(true);
  };

  const handleCancelEdit = () => {
    setShowEditModal(false);
    setEditingItem(null);
  };

  const handleSaveEdit = async () => {
    const { section, index } = editingItem;
    const updatedList = [...editableSummary[section]];
    updatedList[index] = { ...updatedList[index], title: editTitle, value: editInput };

    const res = await UpdateCommentrySummary({
      [section]: updatedList,
      matchId: editableSummary.matchId
    });

    if (res.status === "success") {
      setEditableSummary({ ...editableSummary, [section]: updatedList });
      setShowEditModal(false);
      setEditingItem(null);
    }
  };

  const renderSummaryList = (sectionKey, titleIcon, accentColor) => (
    <div className="space-y-4">
      <h4 className={`text-[10px] font-black uppercase tracking-[0.2em] px-2 flex items-center gap-2 ${accentColor}`}>
        <span>{titleIcon}</span>
        {sectionKey === "positiveHighlights" ? "Positive Highlights" :
          sectionKey === "enhancementOpportunities" ? "Opportunities" : "Takeaways"}
      </h4>
      <div className="space-y-3">
        {editableSummary[sectionKey].map((h, i) => (
          <div
            key={i}
            onClick={() => handleEditClick(sectionKey, i, h.title, h.value)}
            className="group bg-slate-900/50 border border-slate-800 p-4 rounded-2xl hover:border-slate-600 transition-all cursor-pointer shadow-lg active:scale-[0.98]"
          >
            <div className="text-white font-bold text-xs mb-1 group-hover:text-indigo-400 transition-colors">{h.title}</div>
            <div className="text-slate-400 text-[10px] leading-relaxed line-clamp-2">{h.value}</div>
          </div>
        ))}
      </div>
    </div>
  );

  return (
    <div className="fixed inset-0 z-[200] flex items-center justify-center p-4 bg-black/90 backdrop-blur-md animate-fade-in text-slate-200">
      <div className="bg-slate-900 border border-slate-700/50 w-full max-w-6xl max-h-[90vh] rounded-[2rem] shadow-2xl overflow-hidden flex flex-col scale-in">

        {/* Header Section - Sticky/Fixed */}
        <div className="flex-shrink-0 p-6 md:p-8 border-b border-slate-800 bg-slate-900/50 flex flex-col gap-6">
          <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-indigo-500/20 text-indigo-400 rounded-2xl border border-indigo-500/20 shadow-inner">
                <Zap size={28} strokeWidth={2.5} />
              </div>
              <div>
                <h2 className="text-xl md:text-2xl font-black text-white tracking-tight flex items-center gap-2">
                  Compatibility Match Report
                  <span className="px-2 py-0.5 bg-indigo-500/10 border border-indigo-500/20 rounded text-[10px] text-indigo-400 font-black uppercase tracking-widest">Premium</span>
                </h2>
                <p className="text-slate-500 text-[10px] font-black uppercase tracking-[0.3em] mt-1">Deep Analysis & Behavioral Match Insight</p>
              </div>
            </div>

            <div className="flex flex-wrap items-center gap-2 md:gap-3 w-full md:w-auto">
              <button
                onClick={onComparisionProfileClick}
                className="flex-1 md:flex-none flex items-center justify-center gap-2 px-4 py-2 bg-slate-800 hover:bg-slate-700 text-indigo-400 border border-slate-700 rounded-xl text-[10px] font-black uppercase tracking-wider transition-all active:scale-95 shadow-lg"
              >
                <Users size={14} /> Compare Profiles
              </button>

              <button
                onClick={onPhotoComparison}
                className="flex-1 md:flex-none flex items-center justify-center gap-2 px-4 py-2 bg-slate-800 hover:bg-slate-700 text-blue-400 border border-slate-700 rounded-xl text-[10px] font-black uppercase tracking-wider transition-all active:scale-95 shadow-lg"
              >
                <Camera size={14} /> Photo Comparison
              </button>

              <select
                defaultValue={decisionType}
                onChange={(e) => {
                  if (e.target.value) {
                    setDecisionType(e.target.value);
                    setConfirmModal(true);
                  }
                }}
                className="flex-1 md:flex-none h-10 px-4 bg-slate-800 border border-slate-700 text-emerald-400 text-[10px] font-black uppercase tracking-wider rounded-xl focus:outline-none focus:ring-2 focus:ring-emerald-500/50 cursor-pointer shadow-lg"
              >
                <option value="" disabled>Status Action</option>
                <option value="approve">✅ Approve Match</option>
                <option value="reject">❌ Reject Match</option>
              </select>

              <button
                onClick={onClose}
                className="p-2 md:p-3 hover:bg-slate-800 rounded-2xl text-slate-500 hover:text-rose-500 transition-all active:scale-90"
              >
                <X size={24} />
              </button>
            </div>
          </div>

          <div className="flex flex-col md:flex-row justify-between items-center bg-slate-950/50 p-3 rounded-2xl border border-slate-800 gap-4">
            <button
              onClick={async () => {
                let view_status = await onViewOppositeMatch();
                if (!view_status.notificaion) setShowModel(true);
                else setViewCurrent(!view_status.showOppositeMatch);
              }}
              className="w-full md:w-auto flex items-center justify-center gap-2 px-6 py-2 bg-indigo-500/10 hover:bg-indigo-500/20 text-indigo-400 border border-indigo-500/20 rounded-xl text-[10px] font-black uppercase tracking-widest transition-all"
            >
              <RefreshCw size={12} className={!viewCurrent ? "animate-spin-slow" : ""} />
              {viewCurrent ? "View Reverse Compatibility" : "View Original Compatibility"}
            </button>

            <div className="flex items-center gap-4 text-slate-600 text-[9px] font-black uppercase tracking-[0.2em]">
              <span className="flex items-center gap-1.5"><ShieldCheck size={12} className="text-emerald-500" /> Authorized Scan</span>
              <span className="w-1 h-1 bg-slate-800 rounded-full" />
              <span className="flex items-center gap-1.5"><Heart size={12} className="text-rose-500" /> Active Interest</span>
            </div>
          </div>
        </div>

        {/* Tab Navigation - Sticky/Fixed */}
        <div className="flex-shrink-0 px-8 bg-slate-900 border-b border-slate-800 overflow-x-auto no-scrollbar">
          <div className="flex gap-8">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`py-6 flex items-center gap-2.5 text-[11px] font-black uppercase tracking-[0.2em] transition-all relative group
                  ${activeTab === tab.id ? 'text-indigo-400' : 'text-slate-500 hover:text-slate-300'}`}
              >
                <span className={`p-1.5 rounded-lg border transition-all ${activeTab === tab.id ? 'bg-indigo-500/10 border-indigo-500/30 shadow-[0_0_10px_rgba(99,102,241,0.2)]' : 'bg-slate-800 border-transparent shadow-none'}`}>
                  {tab.icon}
                </span>
                {tab.id}
                {activeTab === tab.id && (
                  <div className="absolute bottom-0 left-0 right-0 h-1 bg-indigo-500 rounded-t-full shadow-[0_-2px_10px_rgba(99,102,241,0.5)]" />
                )}
              </button>
            ))}
          </div>
        </div>

        {/* Content Section - Scrollable */}
        <div className="flex-1 overflow-y-auto p-8 custom-scrollbar">

          {/* Summary Tab Content */}
          {activeTab === "Summary" && (
            <div className="space-y-8 animate-fade-in-up">
              <div className="flex items-center gap-3 px-2">
                <div className="w-1.5 h-6 bg-indigo-500 rounded-full" />
                <h3 className="text-xs font-black text-slate-200 uppercase tracking-widest">Candidate Profile Data</h3>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {Object.entries(profile).map(([key, value]) => (
                  <div key={key} className="bg-slate-950/30 p-4 rounded-xl border border-slate-800 group hover:border-slate-700 transition-all">
                    <span className="text-[9px] font-black text-slate-500 uppercase tracking-widest block mb-1 group-hover:text-amber-400 transition-colors">
                      {key.replace(/([A-Z])/g, " $1")}
                    </span>
                    <div className="text-xs font-bold text-slate-200">
                      {Array.isArray(value) ? (
                        <div className="flex flex-col gap-1">
                          {value.map((item, i) => (
                            <div key={i} className="text-[10px] bg-white/5 px-2 py-1 rounded">
                              {typeof item === 'object' ? (item.name || item.Name || JSON.stringify(item)) : String(item)}
                            </div>
                          ))}
                        </div>
                      ) : (typeof value === 'boolean' ? (value ? "Yes" : "No") : (value || "—"))}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Matched Tags Content */}
          {activeTab === "Matched Tags" && (
            <div className="space-y-8 animate-fade-in-up">
              <div className="flex items-center gap-3 px-2">
                <div className="w-1.5 h-6 bg-indigo-500 rounded-full" />
                <h3 className="text-xs font-black text-slate-200 uppercase tracking-widest">Core Compatibility Tags</h3>
              </div>
              {localMatchedTags?.length ? (
                <div className="bg-slate-950 border border-slate-800 rounded-2xl overflow-hidden shadow-2xl">
                  <table className="w-full text-left border-collapse">
                    <thead>
                      <tr className="bg-slate-900 border-b border-slate-800">
                        {["Category", "Tag Name", "Match Type", "Status"].map((header) => (
                          <th key={header} className="px-6 py-4 text-[9px] font-black text-slate-500 uppercase tracking-widest">{header}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-800/50">
                      {localMatchedTags.map((tag, index) => (
                        <tr key={index} className="hover:bg-white/[0.02] transition-colors group">
                          <td className="px-6 py-4">
                            <span className="text-[10px] font-black text-indigo-400 uppercase tracking-tighter">{tag.category}</span>
                          </td>
                          <td className="px-6 py-4">
                            <div className="flex flex-col">
                              <span className="text-[11px] font-bold text-slate-200">{tag.tagName}</span>
                              <span className="text-[9px] text-slate-500 italic truncate max-w-[200px]">{tag.reason}</span>
                            </div>
                          </td>
                          <td className="px-6 py-4">
                            <span className="px-2 py-1 bg-slate-800 border border-slate-700 rounded text-[9px] font-black text-slate-400 uppercase">
                              {tag.matchedType}
                            </span>
                          </td>
                          <td className="px-6 py-4">
                            <select
                              value={tag.status}
                              onChange={(e) => onChangeTagStatus(tag, e.target.value)}
                              className="bg-slate-950 text-indigo-400 border border-slate-800 text-[10px] font-black uppercase tracking-widest px-3 py-1.5 rounded-lg focus:outline-none focus:ring-1 focus:ring-indigo-500 transition-all cursor-pointer hover:bg-slate-900"
                            >
                              <option value="active">Active</option>
                              <option value="inactive">Inactive</option>
                            </select>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className="flex flex-col items-center justify-center p-20 text-slate-600 border-2 border-dashed border-slate-800 rounded-3xl">
                  <Tags size={48} className="mb-4 opacity-20" />
                  <p className="text-sm font-black uppercase tracking-widest">No matched tags available</p>
                </div>
              )}
            </div>
          )}

          {/* Commentary Content */}
          {activeTab === "Commentary" && (
            <div className="space-y-12 animate-fade-in-up">
              <div className="flex items-center gap-3 px-2">
                <div className="w-1.5 h-6 bg-indigo-500 rounded-full" />
                <h3 className="text-xs font-black text-slate-200 uppercase tracking-widest">Natural Language Commentary</h3>
              </div>
              {commentary && (
                <div className="grid lg:grid-cols-2 gap-12">
                  <div className="space-y-6">
                    <div className="flex items-center gap-3 px-2">
                      <div className="w-1.5 h-6 bg-amber-500 rounded-full" />
                      <h3 className="text-xs font-black text-slate-200 uppercase tracking-widest">AI Match Intelligence</h3>
                    </div>
                    <div className="bg-slate-950/80 border border-slate-800/80 rounded-3xl p-8 shadow-inner relative overflow-hidden group">
                      <div className="absolute top-0 right-0 p-4 opacity-5 pointer-events-none group-hover:scale-110 transition-transform">
                        <MessageSquare size={120} />
                      </div>
                      {commentary.llm_commentary && (
                        <div className="text-slate-300 text-xs leading-relaxed whitespace-pre-line italic border-l-2 border-amber-500/50 pl-6 mb-8 relative z-10">
                          {commentary.llm_commentary}
                        </div>
                      )}

                      {commentary.llm_commentary_payload?.commentary_bullets && (
                        <div className="space-y-4 relative z-10">
                          {commentary.llm_commentary_payload.commentary_bullets.map((bullet, idx) => (
                            <div key={idx} className="flex gap-4 group/bullet">
                              <div className="mt-1.5 flex-shrink-0 w-1.5 h-1.5 rounded-full bg-amber-500 group-hover/bullet:shadow-[0_0_8px_rgba(245,158,11,0.5)] transition-all" />
                              <p className="text-[11px] text-slate-400 font-medium leading-relaxed group-hover/bullet:text-slate-200 transition-colors">{bullet}</p>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>

                  <div className="space-y-6">
                    {commentary.llm_commentary_payload?.nonneg?.satisfied && (
                      <div className="bg-emerald-500/5 border border-emerald-500/20 rounded-3xl p-6">
                        <h4 className="text-[10px] font-black text-emerald-400 uppercase tracking-widest mb-4 flex items-center gap-2">
                          <CheckCircle2 size={14} /> Satisfied Requirements
                        </h4>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                          {Object.entries(commentary.llm_commentary_payload.nonneg.satisfied).map(([key, val]) => (
                            <div key={key} className="bg-slate-950/50 px-4 py-2.5 rounded-xl border border-emerald-500/10 flex justify-between items-center">
                              <span className="text-[9px] text-slate-500 font-black uppercase tracking-tighter">{key.replace(/_/g, ' ')}</span>
                              <span className="text-[10px] text-emerald-300 font-bold">{val}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {commentary.llm_commentary_payload?.lifestyle_hits && (
                      <div className="bg-blue-500/5 border border-blue-500/20 rounded-3xl p-6">
                        <h4 className="text-[10px] font-black text-blue-400 uppercase tracking-widest mb-4 flex items-center gap-2">
                          <Zap size={14} className="fill-blue-400" /> Lifestyle Hits
                        </h4>
                        <div className="flex flex-wrap gap-2">
                          {commentary.llm_commentary_payload.lifestyle_hits.map((hit, idx) => (
                            <span key={idx} className="px-4 py-1.5 bg-blue-500/10 text-blue-300 text-[10px] font-bold rounded-full border border-blue-500/30">
                              {hit}
                            </span>
                          ))}
                        </div>
                      </div>
                    )}

                    {commentary.llm_commentary_payload?.profession_details && (
                      <div className="bg-indigo-500/5 border border-indigo-500/20 rounded-3xl p-6">
                        <h4 className="text-[10px] font-black text-indigo-400 uppercase tracking-widest mb-4 flex items-center gap-2">
                          <Briefcase size={14} /> Profession Sync
                        </h4>
                        <div className="space-y-3">
                          <div className="bg-slate-950/50 p-3 rounded-xl border border-slate-800 flex justify-between items-center">
                            <span className="text-[9px] text-slate-600 font-black uppercase tracking-widest">Current User</span>
                            <span className="text-[11px] text-slate-200 font-bold">{commentary.llm_commentary_payload.profession_details.user_profession}</span>
                          </div>
                          <div className="bg-slate-950/50 p-3 rounded-xl border border-slate-800 flex justify-between items-center">
                            <span className="text-[9px] text-slate-600 font-black uppercase tracking-widest">Candidate</span>
                            <span className="text-[11px] text-slate-200 font-bold">{commentary.llm_commentary_payload.profession_details.candidate_profession}</span>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {editableSummary && (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 pt-12 border-t border-slate-800">
                  {editableSummary.positiveHighlights?.length > 0 && renderSummaryList("positiveHighlights", <CheckCircle2 size={12} />, "text-emerald-400")}
                  {editableSummary.enhancementOpportunities?.length > 0 && renderSummaryList("enhancementOpportunities", <AlertCircle size={12} />, "text-amber-400")}
                  {editableSummary.practicalTakeaways?.length > 0 && renderSummaryList("practicalTakeaways", <Star size={12} />, "text-indigo-400")}
                </div>
              )}
            </div>
          )}

          {/* Astrology Content */}
          {activeTab === "Astrology" && (
            <div className="space-y-12 animate-fade-in-up">
              <div className="flex items-center gap-3 px-2">
                <div className="w-1.5 h-6 bg-indigo-500 rounded-full" />
                <h3 className="text-xs font-black text-slate-200 uppercase tracking-widest">Celestial Compatibility Map</h3>
              </div>
              {!IsNull(astrology) ? (
                <div className="space-y-12">
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                    <div className="bg-slate-950 border border-slate-800 p-6 rounded-2xl">
                      <span className="text-[9px] font-black text-slate-500 uppercase tracking-widest block mb-4">Guna Score</span>
                      <div className="flex items-end gap-2">
                        <span className="text-4xl font-black text-indigo-400 tracking-tighter">{astrology.score}</span>
                        <span className="text-slate-600 font-bold mb-1">/ {astrology.maxScore}</span>
                      </div>
                    </div>
                    <div className="bg-slate-950 border border-slate-800 p-6 rounded-2xl col-span-1 md:col-span-2 lg:col-span-3">
                      <span className="text-[9px] font-black text-slate-500 uppercase tracking-widest block mb-4">Compatibility Status</span>
                      <div className="text-xl font-bold text-slate-200">{astrology.compatibility}</div>
                      <p className="text-xs text-slate-500 mt-2">{astrology.overallMessage}</p>
                    </div>
                  </div>

                  <div className="grid md:grid-cols-2 gap-8">
                    <div className="bg-slate-900 border border-slate-800 p-8 rounded-3xl">
                      <h4 className="text-[10px] font-black text-emerald-400 uppercase tracking-widest mb-6 border-l-4 border-emerald-500 pl-4">Celestial Strengths</h4>
                      <p className="text-[11px] text-slate-400 leading-relaxed italic">"{astrology.strengths}"</p>
                    </div>
                    <div className="bg-slate-900 border border-slate-800 p-8 rounded-3xl">
                      <h4 className="text-[10px] font-black text-rose-400 uppercase tracking-widest mb-6 border-l-4 border-rose-500 pl-4">Potential Challenges</h4>
                      <p className="text-[11px] text-slate-400 leading-relaxed italic">"{astrology.challenges}"</p>
                    </div>
                  </div>

                  <div className="bg-slate-950 border border-slate-800 rounded-3xl overflow-hidden shadow-2xl">
                    <div className="px-6 py-4 bg-slate-900 border-b border-slate-800">
                      <h4 className="text-[10px] font-black text-indigo-400 uppercase tracking-[0.2em] flex items-center gap-2">
                        <Star size={14} className="fill-indigo-400" /> Category Breakdown
                      </h4>
                    </div>
                    <div className="overflow-x-auto">
                      <table className="w-full text-left border-collapse">
                        <thead>
                          <tr className="bg-slate-950 border-b border-slate-800">
                            {["Category", "Score", "Description"].map(h => (
                              <th key={h} className="px-6 py-4 text-[9px] font-black text-slate-600 uppercase tracking-widest whitespace-nowrap">{h}</th>
                            ))}
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-800/50">
                          {astrologyCategories.map((item, i) => (
                            <tr key={i} className="hover:bg-white/[0.02] transition-colors group">
                              <td className="px-6 py-4 text-[10px] font-black text-slate-300 uppercase">{item.category}</td>
                              <td className="px-6 py-4">
                                <span className={`px-2 py-0.5 rounded font-mono font-bold text-[10px] ${item.score === item.maxScore ? 'bg-emerald-500/10 text-emerald-400' : 'bg-slate-800 text-slate-400'}`}>
                                  {item.score} / {item.maxScore}
                                </span>
                              </td>
                              <td className="px-6 py-4 text-[10px] text-slate-500 italic max-w-sm">{item.description}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="flex flex-col items-center justify-center p-20 text-slate-600 border-2 border-dashed border-slate-800 rounded-3xl">
                  <Star size={48} className="mb-4 opacity-20" />
                  <p className="text-sm font-black uppercase tracking-widest">No Celestial Data Map Found</p>
                </div>
              )}
            </div>
          )}

          {/* Cluster Analysis Content */}
          {activeTab === "Cluster Analysis" && (
            <div className="space-y-8 animate-fade-in-up">
              <div className="flex items-center gap-3 px-2">
                <div className="w-1.5 h-6 bg-indigo-500 rounded-full" />
                <h3 className="text-xs font-black text-slate-200 uppercase tracking-widest">Behavioral Cluster Scores</h3>
              </div>
              {!IsNull(clusterAnalysis) ? (
                <div className="bg-slate-950 border border-slate-800 rounded-3xl overflow-hidden shadow-2xl">
                  <div className="px-6 py-4 bg-slate-900 border-b border-slate-800 flex justify-between items-center">
                    <h4 className="text-[10px] font-black text-indigo-400 uppercase tracking-[0.2em] flex items-center gap-2">
                      <Layers size={14} className="fill-indigo-400" /> Behavioral Cluster Scores
                    </h4>
                    <span className="text-[9px] font-black text-slate-600 bg-slate-950 px-3 py-1 rounded-full border border-slate-800 uppercase tracking-widest font-mono">Statistical Analysis</span>
                  </div>
                  <div className="overflow-x-auto">
                    <table className="w-full text-left border-collapse">
                      <thead>
                        <tr className="bg-slate-950 border-b border-slate-800">
                          {["Category Cluster", "Raw", "Weighted", "Analysis Reasoning"].map(h => (
                            <th key={h} className="px-6 py-4 text-[9px] font-black text-slate-600 uppercase tracking-widest">{h}</th>
                          ))}
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-slate-800/50">
                        {clusterAnalysis.map((item, i) => (
                          <tr key={i} className="hover:bg-white/[0.02] transition-colors group">
                            <td className="px-6 py-4">
                              <div className="flex items-center gap-3">
                                <div className="w-2 h-2 rounded-full bg-indigo-500 shadow-lg" />
                                <span className="text-[10px] font-black text-slate-200 uppercase tracking-tight">{item.category}</span>
                              </div>
                            </td>
                            <td className="px-6 py-4 font-mono text-xs text-slate-500">{item.rawScore}</td>
                            <td className="px-6 py-4">
                              <div className="w-20 h-1.5 bg-slate-800 rounded-full overflow-hidden">
                                <div className="h-full bg-indigo-500" style={{ width: `${Math.min((item.weightedScore / 10) * 100, 100)}%` }} />
                              </div>
                            </td>
                            <td className="px-6 py-4 text-[10px] text-slate-500 italic max-w-lg leading-relaxed">{item.reasons}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              ) : (
                <div className="flex flex-col items-center justify-center p-20 text-slate-600 border-2 border-dashed border-slate-800 rounded-3xl">
                  <Layers size={48} className="mb-4 opacity-20" />
                  <p className="text-sm font-black uppercase tracking-widest">No Statistical Clusters Mapped</p>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Footer Actions */}
        <div className="px-8 py-6 bg-slate-950 border-t border-slate-800 flex flex-col md:flex-row justify-between items-center gap-6">
          <div className="hidden md:flex items-center gap-3 text-slate-600">
            <AlertCircle size={14} className="text-amber-500" />
            <span className="text-[9px] font-black uppercase tracking-[0.2em]">Match Logic generated via Multi-Cloud AI Mesh Integration</span>
          </div>

          <div className="flex items-center gap-3 w-full md:w-auto">
            <button
              onClick={async () => await onResetMatch(profile.userId)}
              className={`flex-1 md:flex-none px-6 py-3 rounded-2xl text-[10px] font-black uppercase tracking-widest transition-all active:scale-95 flex items-center justify-center gap-2
                ${!publish ? "bg-slate-800 text-slate-600 cursor-not-allowed border border-transparent" : "bg-rose-500/10 hover:bg-rose-500/20 text-rose-500 border border-rose-500/20 shadow-lg"}`}
            >
              <RefreshCw size={14} /> Reset Match
            </button>
            <button
              onClick={async () => { if (!publish) { if (await onPublish(profile.userId)) { onClose() } } }}
              className={`flex-1 md:flex-none px-10 py-3 rounded-2xl text-[10px] font-black uppercase tracking-[0.2em] transition-all active:scale-95 flex items-center justify-center gap-2 shadow-xl
                ${publish ? "bg-slate-800 text-slate-600 cursor-not-allowed border border-transparent" : "bg-emerald-500 hover:bg-emerald-400 text-black border-none"}`}
            >
              <Send size={14} fill="currentColor" /> {publish ? "Currently Live" : "Publish Action"}
            </button>
          </div>
        </div>
      </div>

      <SimpleMessageModal show={showModal} onClose={() => setShowModel(false)} message={"No Match Found"} />

      <ConfirmationModal
        title={"Change Match Decision?"}
        description={`Confirming final match state update. This action may trigger notification triggers.`}
        isOpen={confirmModal}
        onClose={() => {
          setConfirmModal(false);
          setDecisionType("");
        }}
        onConfirm={() => handleConfirm(profile.userId)}
      />

      {showEditModal && (
        <div className="fixed inset-0 z-[300] bg-black/80 backdrop-blur-md flex items-center justify-center p-4">
          <div className="bg-slate-900 border border-slate-700 w-full max-w-xl rounded-[2rem] shadow-2xl overflow-hidden scale-in">
            <div className="p-8 border-b border-slate-800 bg-slate-900/50 flex items-center justify-between">
              <h3 className="text-lg font-black text-white uppercase tracking-tight">Modify Insight Node</h3>
              <button onClick={handleCancelEdit} className="text-slate-500 hover:text-white transition-colors"><X size={20} /></button>
            </div>

            <div className="p-8 space-y-6">
              <div className="space-y-2">
                <label className="text-[10px] font-black text-indigo-400 uppercase tracking-widest px-1">Headline</label>
                <textarea
                  value={editTitle}
                  onChange={(e) => setEditTitle(e.target.value)}
                  rows={2}
                  className="w-full bg-slate-950 text-slate-200 border border-slate-800 rounded-2xl px-4 py-3 text-xs focus:ring-2 focus:ring-indigo-500/50 outline-none transition-all resize-none shadow-inner"
                />
              </div>
              <div className="space-y-2">
                <label className="text-[10px] font-black text-indigo-400 uppercase tracking-widest px-1">Full Context</label>
                <textarea
                  value={editInput}
                  onChange={(e) => setEditInput(e.target.value)}
                  rows={6}
                  className="w-full bg-slate-950 text-slate-200 border border-slate-800 rounded-2xl px-4 py-3 text-xs focus:ring-2 focus:ring-indigo-500/50 outline-none transition-all resize-none shadow-inner"
                />
              </div>
            </div>

            <div className="p-8 bg-slate-950/80 border-t border-slate-800 flex justify-end gap-3">
              <button onClick={handleCancelEdit} className="px-6 py-2.5 rounded-xl bg-slate-800 text-slate-400 text-[10px] font-black uppercase tracking-widest transition-all active:scale-95">Cancel</button>
              <button onClick={handleSaveEdit} className="px-10 py-2.5 rounded-xl bg-indigo-500 text-white text-[10px] font-black uppercase tracking-widest shadow-lg shadow-indigo-500/20 transition-all active:scale-95">Update Node</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AstroMatchModal;
